import React, { useState } from "react";
import { Tabs } from "../../Tabs/Tabs";
import { getTrack1Cards, getTrack2Cards } from "./const";
import { TrackCard } from "../../TrackCard/TrackCard";
import { Stepper } from "../../Stepper/Stepper";
import { ShareEmailModal } from "../../ShareEmailModal/ShareEmailModal";
import { AcceptChallengeModal } from "../../AcceptChallengeModal/AcceptChallengeModal";
import {
  trackTabsOptions,
  useMembershipPageContext,
} from "@profile/pages/MembershipPage/MembershipPage.context";
import MarketingService from "@services/Marketing.service";
import store from "@features/store";
import { setApiMe } from "@features/action.bfapp";
import { Step6CongratsModal } from "../../Step6CongratsModal/Step6CongratsModal";

type TrackProgressProps = {
  isUk: boolean;
  slug?: string;
  currentStep: number;
};

const totalVisibleSteps = 3;

export const TrackProgress = ({
  isUk,
  slug,
  currentStep,
}: TrackProgressProps) => {
  const {
    timelockMinValue,
    activeTrackTab,
    handleChangeActiveTrackTab,
    setInvestmentPlatformAccordionOpen,
  } = useMembershipPageContext();
  const [isShareEmailModalOpen, setIsShareModalOpen] = useState(false);
  const [isAcceptChallengeModalOpen, setIsAcceptChallengeModalOpen] =
    useState(false);
  const [isStep6CongratsModalOpen, setIsStep6CongratsModalOpen] =
    useState(false);
  const [visibleStep, setVisibleStep] = useState(1);

  const isTrack1 = activeTrackTab === "track_1";
  const isTrack2 = activeTrackTab === "track_2";

  const currentDisplayStep = isTrack1 ? currentStep : currentStep - 3;

  const openShareEmailModal = () => {
    setIsShareModalOpen(true);
  };

  const closeShareEmailModal = () => {
    setIsShareModalOpen(false);
  };

  const openAcceptChallengeModal = () => {
    setIsAcceptChallengeModalOpen(true);
  };

  const closeAcceptChallengeModal = () => {
    setIsAcceptChallengeModalOpen(false);
  };

  const openStep6CongratsModal = () => {
    setIsStep6CongratsModalOpen(true);
  };

  const closeStep6CongratsModal = () => {
    store.dispatch(setApiMe(true));
    setIsStep6CongratsModalOpen(false);
  };

  const handleScroll = (direction: "left" | "right") => {
    const container = document.getElementById("track-progress-steps");
    if (container) {
      const stepWidth = container.offsetWidth;
      if (direction === "left" && visibleStep > 1) {
        container.scrollLeft -= stepWidth;
        setVisibleStep((prev) => Math.max(prev - 1, 1));
      } else if (direction === "right" && visibleStep < totalVisibleSteps) {
        container.scrollLeft += stepWidth;
        setVisibleStep((prev) => Math.min(prev + 1, totalVisibleSteps));
      }
    }
  };

  const handleUpdateWaitingList = async () => {
    await MarketingService.updatePortfolioBuilderWaitingList();
    openStep6CongratsModal();
  };

  const handleClickOnStepOneLink = () => {
    setInvestmentPlatformAccordionOpen(true);

    setTimeout(() => {
      document
        .getElementById("investor-accounts-secion-tabs")
        ?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const track1Cards = getTrack1Cards(
    slug,
    timelockMinValue,
    isUk,
    handleClickOnStepOneLink
  );
  const track2Cards = getTrack2Cards(slug, isUk);

  return (
    <>
      <div className="mt-[80px]">
        <div className="text-center">
          <h4 className="mb-[16px]">
            Track Your Bitcoin Wealth Building Journey
          </h4>

          <Tabs
            options={trackTabsOptions}
            activeValue={activeTrackTab}
            onChange={handleChangeActiveTrackTab}
          />

          <div className="mx-auto max-w-[840px] py-[40px] text-center">
            <div className="font-medium mb-4">
              Bitcoin Wealth Builder Program Qualification Steps
            </div>
            <div>
              The Bitcoin Wealth Builder Program qualification status indicates
              your current progress towards qualifying for the Bitcoin Wealth
              Builder Program (valued at $5,000){isUk ? "" : ", for FREE"}. For
              more information on the Bitcoin Wealth Builder Program,{" "}
              <a
                href={
                  isUk
                    ? "https://www.bitcoinwealthbuilder.io/bwb "
                    : "http://www.simondixon.com/bwb"
                }
                target="_blank"
                className="underline font-semibold"
              >
                click here
              </a>
              .
            </div>
          </div>

          <div className="text-black mb-8">
            {isTrack1
              ? "Track 1: Bitcoin Wealth Builder Program"
              : "Track 2: Portfolio Builder"}
          </div>

          <div id="track-progress-steps" className="overflow-x-auto">
            <div className="w-[300%] md:w-full">
              <Stepper steps={3} currentStep={currentDisplayStep} />

              {isTrack1 && (
                <div className="flex md:grid grid-cols-3 gap-4 mx-auto p-4">
                  {track1Cards.map((card, index) => (
                    <TrackCard
                      key={`${index}-${card.step}`}
                      title={card.title}
                      imageSrc={card.imageSrc}
                      description={card.description}
                      buttonText={card.buttonText}
                      buttonDisabled={
                        card.buttonDisabled || card.step > currentStep + 1
                      }
                      buttonCompleted={card.step <= currentStep}
                      buttonLink={card.buttonLink}
                      onButtonClick={
                        index === 2 ? openShareEmailModal : undefined
                      }
                    />
                  ))}
                </div>
              )}

              {isTrack2 && (
                <div className="grid grid-cols-3 gap-4 mx-auto p-4">
                  {track2Cards.map((card, index) => (
                    <TrackCard
                      key={`${index}-${card.step}`}
                      title={card.title}
                      imageSrc={card.imageSrc}
                      description={card.description}
                      buttonText={card.buttonText}
                      buttonDisabled={card.step > currentStep + 1}
                      buttonCompleted={card.step <= currentStep}
                      buttonLink={card.buttonLink}
                      onButtonClick={
                        index === 1
                          ? openAcceptChallengeModal
                          : index === 2
                          ? handleUpdateWaitingList
                          : undefined
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="inline-flex gap-6 items-center justify-center md:hidden mt-4 border border-[#2EC973] rounded-[8px] p-2">
            <button
              className={`bg-transparent disabled:bg-transparent p-0 ${
                visibleStep === 1 ? "text-gray-400 cursor-not-allowed" : ""
              }`}
              disabled={visibleStep === 1}
              onClick={() => handleScroll("left")}
            >
              &#8592;
            </button>
            <span className="mx-4 text-sm font-medium">
              {visibleStep} / {totalVisibleSteps}
            </span>
            <button
              className={`bg-transparent disabled:bg-transparent p-0 ${
                visibleStep === totalVisibleSteps
                  ? "text-gray-400 cursor-not-allowed"
                  : ""
              }`}
              disabled={visibleStep === totalVisibleSteps}
              onClick={() => handleScroll("right")}
            >
              &#8594;
            </button>
          </div>
        </div>
      </div>

      <ShareEmailModal
        open={isShareEmailModalOpen}
        isUk={isUk}
        onClose={closeShareEmailModal}
      />

      <AcceptChallengeModal
        open={isAcceptChallengeModalOpen}
        onClose={closeAcceptChallengeModal}
      />

      <Step6CongratsModal
        open={isStep6CongratsModalOpen}
        onClose={closeStep6CongratsModal}
        isUk={isUk}
      />
    </>
  );
};
